// source-media/core.ts — the single front-end builder for a SOURCE's header media (cover image / video).
//
// This is the source-cover analogue of thumbnail/core.ts. It ports the PHP cover-picker
// (ft_source_header_pic_url() in includes/functions/db/sources/build_source_arr.php) PLUS the legacy
// table/card media decision (image-vs-video priority + random default cover). Moving it client-side keeps
// the JSON endpoints + server templates pure data: PHP ships the raw header pictures/videos and the
// front-end decides what to show. See [[feedback_frontend_logic_ts_not_php]].
//
// Two consumers share this core:
//   • the catalog-list bundle's card renderers (render-library.ts) call resolveSourceMedia() directly;
//   • the global source-media bundle (index.ts → window.ftMountSourceMedia) paints server-emitted
//     .ft-source-media-mount placeholders via buildSourceMediaNode() — the same mount+hydrate pattern as
//     thumbnail/inventory-tile/rune. That covers server-rendered surfaces (the rules nav source rail).
//
// The PHP ft_source_header_pic_url() helper survives ONLY for backend Stripe line-item images
// (ft_stripe_cart_inc.php) — never the browser, so it isn't "front-end logic".

// Raw header media exactly as PHP ships it.
export interface SourceHeaderPicture { url: string; is_thumbnail: boolean; }
export interface SourceHeaderVideo { url: string; mime: string; primary: boolean; }

export interface SourceMedia {
    kind: 'image' | 'video';
    src: string;
    mime: string;
    poster: string; // cover image used as the <video> poster; '' for an image
}

// Mirror of ft_source_header_pic_url(): the first NON-thumbnail (full cover) image, or — when
// preferThumbnail — the pic_thumbnail-flagged image if one exists, else the first available. '' when none.
export function pickSourceHeaderPicUrl(
    pictures: SourceHeaderPicture[] | null | undefined,
    preferThumbnail = false,
): string {
    let fallback = '';
    for (const pic of pictures || []) {
        const url = (pic?.url || '').trim();
        if (!url) continue;
        if (preferThumbnail) {
            if (pic.is_thumbnail) return url;
            if (!fallback) fallback = url;
        } else if (!pic.is_thumbnail) {
            return url;
        }
    }
    return fallback;
}

// Mirror of the legacy catalog media decision: a primary (else first) header video with a URL wins;
// otherwise the first non-thumbnail cover picture; otherwise a random default campaign-header cover. A
// video keeps the cover image (if any) as its poster.
export function resolveSourceMedia(
    pictures: SourceHeaderPicture[] | null | undefined,
    videos: SourceHeaderVideo[] | null | undefined,
    defaultCoverBase: string,
): SourceMedia {
    let headerVideo: SourceHeaderVideo | null = null;
    for (const v of videos || []) {
        if (!v?.url) continue;
        if (v.primary) { headerVideo = v; break; }
        if (!headerVideo) headerVideo = v;
    }

    let img = pickSourceHeaderPicUrl(pictures);
    if (!img && !headerVideo) {
        const n = 1 + Math.floor(Math.random() * 7); // default_campaign_header_1..7.jpg
        img = `${defaultCoverBase}default_campaign_header_${n}.jpg`;
    }

    if (headerVideo) {
        return { kind: 'video', src: headerVideo.url, mime: headerVideo.mime, poster: img };
    }
    return { kind: 'image', src: img, mime: '', poster: '' };
}

// The pure-data payload a .ft-source-media-mount placeholder carries (assembled by ft_source_media_mount()).
export interface SourceMediaMountData {
    pictures?: SourceHeaderPicture[] | null;
    videos?: SourceHeaderVideo[] | null;
    defaultCoverBase?: string;
    mediaClass?: string; // class(es) on the built <img>/<video>
    mediaStyle?: string; // inline style (cssText) on the built element — the card hero sizing
    alt?: string;        // <img> alt (videos take none)
}

// Build the media element for a mount — an <img> or hover-play <video> matching the legacy source-card /
// nav-rail markup. The surrounding link + media container stay server-rendered; the hydrator only swaps in
// this inner element.
export function buildSourceMediaNode(data: SourceMediaMountData): HTMLElement {
    const media = resolveSourceMedia(data.pictures, data.videos, data.defaultCoverBase || '');
    const cls = data.mediaClass || '';
    const style = data.mediaStyle || '';

    if (media.kind === 'video') {
        const video = document.createElement('video');
        if (cls) video.className = cls;
        if (style) video.style.cssText = style;
        video.src = media.src;
        video.muted = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('preload', media.poster ? 'none' : 'metadata');
        if (media.poster) video.setAttribute('poster', media.poster);
        return video;
    }

    const img = document.createElement('img');
    if (cls) img.className = cls;
    if (style) img.style.cssText = style;
    img.src = media.src;
    img.alt = data.alt || '';
    img.loading = 'lazy';
    img.decoding = 'async';
    return img;
}
